import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppState } from '@app/store/state/app.state';
import {
  addKioskVisitor,
  cancelPairingRequestOnServer,
  createPairingRequest,
  getKioskVisitorsForToday,
  KioskAddVisitorRequest,
  KioskPairingRequestCreatedResponse,
  KioskPairingRequestPollResponse,
  KioskVisitor,
  pollPairingRequest,
  signInKioskVisitor,
  signOutKioskVisitor,
  unpairKiosk,
} from './kiosk-service';
import { clearKioskSession, loadKioskSession, saveKioskSession } from '../storage/kioskStorage';
import { KioskUnauthorizedError } from './kiosk-agent';

type KioskPairingUiStatus = 'idle' | 'pending' | 'approved' | 'rejected' | 'expired' | 'error';

export interface KioskState {
  paired: boolean;
  companyId: string | null;
  companyName: string;
  officeId: number | null;
  officeLabel: string;
  visitors: KioskVisitor[];
  loading: boolean;
  pairing: boolean;
  pairingStatus: KioskPairingUiStatus;
  pendingRequestId: string | null;
  pendingExpiresAt: string | null;
  pollIntervalMs: number;
  pollFailureCount: number;
  pairingError: string | null;
  lastError: string | null;
}

const DEFAULT_POLL_INTERVAL_MS = 2000;
// A pairing wait survives this many consecutive failed polls (network blips,
// server restarts) before giving up; one bad response must not abandon a
// request the admin may be about to approve.
const MAX_CONSECUTIVE_POLL_FAILURES = 5;

const initialFromStorage = (): KioskState => {
  const stored = loadKioskSession();
  const base: KioskState = {
    paired: false,
    companyId: null,
    companyName: '',
    officeId: null,
    officeLabel: '',
    visitors: [],
    loading: false,
    pairing: false,
    pairingStatus: 'idle',
    pendingRequestId: null,
    pendingExpiresAt: null,
    pollIntervalMs: DEFAULT_POLL_INTERVAL_MS,
    pollFailureCount: 0,
    pairingError: null,
    lastError: null,
  };
  if (stored) {
    return {
      ...base,
      paired: true,
      companyId: stored.companyId,
      companyName: stored.companyName,
      officeId: stored.officeId,
      officeLabel: stored.officeLabel,
    };
  }
  return base;
};

export const initialKioskState: KioskState = initialFromStorage();

export const submitPairingCode: any = createAsyncThunk(
  'kiosk/submitPairingCode',
  async (params: { code: string }, thunkAPI) => {
    try {
      const response = await createPairingRequest(params.code);
      if (!response?.requestId) return thunkAPI.rejectWithValue('Invalid or expired code');
      return response;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err?.message || 'Invalid or expired code');
    }
  }
);

export const pollPairingStatus: any = createAsyncThunk(
  'kiosk/pollPairingStatus',
  async (params: { requestId: string }, thunkAPI) => {
    try {
      const response = await pollPairingRequest(params.requestId);
      // A poll that resolves after the user cancelled (or started a new attempt)
      // must not write credentials for the stale request.
      const kiosk = (thunkAPI.getState() as AppState).kiosk;
      const stillCurrent = kiosk.pairingStatus === 'pending' && kiosk.pendingRequestId === params.requestId;
      if (stillCurrent && response.status === 'APPROVED' && response.deviceToken && response.companyId && response.officeId !== undefined) {
        saveKioskSession({
          deviceToken: response.deviceToken,
          companyId: response.companyId,
          companyName: response.companyName ?? '',
          officeId: response.officeId,
          officeLabel: response.officeLabel ?? '',
        });
      }
      return response;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err?.message || 'Failed to check approval status');
    }
  }
);

export const cancelPairing: any = createAsyncThunk(
  'kiosk/cancelPairing',
  async (_: void, thunkAPI) => {
    const kiosk = (thunkAPI.getState() as AppState).kiosk;
    const requestId = kiosk.pendingRequestId;
    thunkAPI.dispatch(cancelPairingRequest());
    if (requestId) {
      try { await cancelPairingRequestOnServer(requestId); } catch { /* best effort */ }
    }
  }
);

export const loadKioskVisitors: any = createAsyncThunk(
  'kiosk/loadKioskVisitors',
  async (_: void, thunkAPI) => {
    try {
      return await getKioskVisitorsForToday();
    } catch (err: any) {
      if (err instanceof KioskUnauthorizedError) {
        clearKioskSession();
        return thunkAPI.rejectWithValue('UNAUTHORIZED');
      }
      return thunkAPI.rejectWithValue(err?.message || 'Failed to load visitors');
    }
  }
);

export const addVisitorOnKiosk: any = createAsyncThunk(
  'kiosk/addVisitorOnKiosk',
  async (params: { request: KioskAddVisitorRequest }, thunkAPI) => {
    try {
      await addKioskVisitor(params.request);
      thunkAPI.dispatch(loadKioskVisitors());
    } catch (err: any) {
      if (err instanceof KioskUnauthorizedError) {
        clearKioskSession();
        return thunkAPI.rejectWithValue('UNAUTHORIZED');
      }
      return thunkAPI.rejectWithValue(err?.message || 'Could not add visitor');
    }
  }
);

export const signInVisitorOnKiosk: any = createAsyncThunk(
  'kiosk/signInVisitorOnKiosk',
  async (params: { id: number }, thunkAPI) => {
    try {
      await signInKioskVisitor(params.id);
      thunkAPI.dispatch(loadKioskVisitors());
    } catch (err: any) {
      if (err instanceof KioskUnauthorizedError) {
        clearKioskSession();
        return thunkAPI.rejectWithValue('UNAUTHORIZED');
      }
      return thunkAPI.rejectWithValue(err?.message || 'Could not sign visitor in');
    }
  }
);

export const signOutVisitorOnKiosk: any = createAsyncThunk(
  'kiosk/signOutVisitorOnKiosk',
  async (params: { id: number }, thunkAPI) => {
    try {
      await signOutKioskVisitor(params.id);
      thunkAPI.dispatch(loadKioskVisitors());
    } catch (err: any) {
      if (err instanceof KioskUnauthorizedError) {
        clearKioskSession();
        return thunkAPI.rejectWithValue('UNAUTHORIZED');
      }
      return thunkAPI.rejectWithValue(err?.message || 'Could not sign visitor out');
    }
  }
);

export const unpairThisKiosk: any = createAsyncThunk(
  'kiosk/unpairThisKiosk',
  async () => {
    try { await unpairKiosk(); } catch { /* best effort */ }
    clearKioskSession();
  }
);

const kioskSlice = createSlice({
  name: 'kiosk',
  initialState: initialKioskState,
  reducers: {
    clearPairingError(state) { state.pairingError = null; },
    cancelPairingRequest(state) {
      state.pairing = false;
      state.pairingStatus = 'idle';
      state.pendingRequestId = null;
      state.pendingExpiresAt = null;
      state.pollFailureCount = 0;
      state.pairingError = null;
    },
    forceUnpair(state) {
      clearKioskSession();
      state.paired = false;
      state.companyId = null;
      state.companyName = '';
      state.officeId = null;
      state.officeLabel = '';
      state.visitors = [];
      state.pairingStatus = 'idle';
      state.pendingRequestId = null;
      state.pendingExpiresAt = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(submitPairingCode.pending, state => {
        state.pairing = true;
        state.pairingStatus = 'idle';
        state.pairingError = null;
      })
      .addCase(submitPairingCode.fulfilled, (state, action: PayloadAction<KioskPairingRequestCreatedResponse>) => {
        state.pairing = false;
        state.pairingStatus = 'pending';
        state.pendingRequestId = action.payload.requestId;
        state.pendingExpiresAt = action.payload.expiresAt;
        state.pollIntervalMs = action.payload.pollIntervalMs || DEFAULT_POLL_INTERVAL_MS;
        state.pollFailureCount = 0;
        state.pairingError = null;
      })
      .addCase(submitPairingCode.rejected, (state, action: any) => {
        state.pairing = false;
        state.pairingStatus = 'error';
        state.pairingError = action.payload || 'Invalid or expired code';
      })
      .addCase(pollPairingStatus.fulfilled, (state, action: any) => {
        // Ignore responses for a request that is no longer the current attempt
        // (user cancelled or restarted while this poll was in flight).
        const requestId = action.meta?.arg?.requestId;
        if (state.pairingStatus !== 'pending' || state.pendingRequestId !== requestId) return;

        state.pollFailureCount = 0;
        const response: KioskPairingRequestPollResponse = action.payload;
        if (response.status === 'APPROVED' && response.deviceToken) {
          state.pairingStatus = 'approved';
          state.paired = true;
          state.companyId = response.companyId ?? null;
          state.companyName = response.companyName ?? '';
          state.officeId = response.officeId ?? null;
          state.officeLabel = response.officeLabel ?? '';
          state.pendingRequestId = null;
          state.pendingExpiresAt = null;
          state.pairingError = null;
        } else if (response.status === 'APPROVED') {
          // Approved but no credentials delivered — never enter the paired UI
          // without a stored token.
          state.pairingStatus = 'error';
          state.pendingRequestId = null;
          state.pendingExpiresAt = null;
          state.pairingError = 'Pairing was approved but no credentials were received. Try again.';
        } else if (response.status === 'REJECTED' || response.status === 'CANCELLED') {
          state.pairingStatus = 'rejected';
          state.pendingRequestId = null;
          state.pendingExpiresAt = null;
        } else if (response.status === 'EXPIRED') {
          state.pairingStatus = 'expired';
          state.pendingRequestId = null;
          state.pendingExpiresAt = null;
        }
      })
      .addCase(pollPairingStatus.rejected, (state, action: any) => {
        if (state.pairingStatus !== 'pending') return;
        state.pollFailureCount += 1;
        if (state.pollFailureCount >= MAX_CONSECUTIVE_POLL_FAILURES) {
          state.pairingStatus = 'error';
          state.pendingRequestId = null;
          state.pendingExpiresAt = null;
          state.pairingError = action.payload || 'Failed to check approval status';
        }
        // Below the threshold: stay pending so the next interval tick retries.
      })
      .addCase(loadKioskVisitors.pending, state => { state.loading = true; })
      .addCase(loadKioskVisitors.fulfilled, (state, action: PayloadAction<KioskVisitor[]>) => {
        state.loading = false;
        state.visitors = action.payload;
        state.lastError = null;
      })
      .addCase(loadKioskVisitors.rejected, (state, action: any) => {
        state.loading = false;
        if (action.payload === 'UNAUTHORIZED') {
          state.paired = false;
          state.companyId = null;
          state.companyName = '';
          state.officeId = null;
          state.officeLabel = '';
          state.visitors = [];
        } else {
          state.lastError = action.payload || 'Failed to load visitors';
        }
      })
      .addCase(unpairThisKiosk.fulfilled, state => {
        state.paired = false;
        state.companyId = null;
        state.companyName = '';
        state.officeId = null;
        state.officeLabel = '';
        state.visitors = [];
        state.pairingStatus = 'idle';
        state.pendingRequestId = null;
        state.pendingExpiresAt = null;
      });
  },
});

export default kioskSlice.reducer;
export const { clearPairingError } = kioskSlice.actions;
const { cancelPairingRequest } = kioskSlice.actions;

export const selectKiosk = (state: AppState) => state.kiosk;
